import { cartAtom } from "@/atoms"
import { tenantTimezoneAtom } from "@/atoms/tenant-clock"
import type { CouponCodeSubmit } from "@/components/cart/coupon/CouponCodeForm"
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog"
import { useCouponTerms } from "@/hooks/use-coupon-terms"
import { bagCouponFocus, bagCouponView, evaluationSubtotalCents, type BagCoupon, type BagCouponFocus } from "@/lib/bag-coupon"
import { getStoredCompany } from "@/lib/company-storage"
import { buildEvaluationLines } from "@/lib/discount-evaluation/cart-lines"
import { addCouponCode, removeCouponCode } from "@/lib/discount-evaluation/coupon-codes"
import { saveWalletCodes } from "@/lib/coupon-wallet"
import { keepCouponForLater, savePendingCouponCodes } from "@/lib/pending-coupon"
import { useAtom, useAtomValue } from "jotai"
import { couponSnapshotAtom } from "../lib/discount-plan"
import { checkoutAtom } from "../state"
import { useDiscountPlan } from "./useDiscountPlan"

/** The checkout's coupons as the bag draws them, the one to show, and how to add or take one off the order. */
export type CheckoutCoupons = {
  coupons: BagCoupon[]
  focus: BagCouponFocus | null
  remove: (code: string) => void
  submitCode: (raw: string, replacing?: string) => CouponCodeSubmit
  pick: (code: string) => void
}

function persist(codes: readonly string[]) {
  const merchantId = getStoredCompany()?.id
  if (merchantId) savePendingCouponCodes(codes, merchantId)
}

function keepInWallet(code: string) {
  const merchantId = getStoredCompany()?.id
  if (!merchantId) return
  keepCouponForLater(code, merchantId)
  void saveWalletCodes(merchantId, [code])
}

/**
 * The typed codes paired with the checkout's own verdict (the last one stays
 * on screen while the next is asked), each read like the bag reads it. Taking
 * a code off only un-applies it: it stays among the customer's coupons; a
 * typed code joins them, and picking one from them makes it the order's.
 */
export function useCheckoutCoupons(): CheckoutCoupons {
  const [state, setState] = useAtom(checkoutAtom)
  const cart = useAtomValue(cartAtom)
  const { categories } = useBrowsingChannelCatalog()
  const plan = useDiscountPlan()
  const snapshot = useAtomValue(couponSnapshotAtom)
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const terms = useCouponTerms(getStoredCompany()?.id ?? null, state.couponCodes)

  const lastVerdict = snapshot.status === "ready" ? snapshot.data : null
  const evaluation = plan.coupons.data ?? lastVerdict
  const verdict = evaluation ? { evaluation, fresh: plan.coupons.data !== null } : null
  const subtotalCents = evaluationSubtotalCents(buildEvaluationLines(cart, categories))
  const now = new Date()
  const coupons = state.couponCodes.map((code) =>
    bagCouponView({ code, terms: terms[code], verdict, unconfirmed: plan.coupons.phase === "error", subtotalCents, cart, now, timeZone }),
  )

  function remove(code: string) {
    const codes = removeCouponCode(state.couponCodes, code)
    setState((prev) => ({ ...prev, couponCodes: removeCouponCode(prev.couponCodes, code) }))
    persist(codes)
  }

  function submitCode(raw: string, replacing?: string): CouponCodeSubmit {
    const others = replacing ? removeCouponCode(state.couponCodes, replacing) : state.couponCodes
    const result = addCouponCode(others, raw)
    if (!result.ok) return result
    setState((prev) => ({ ...prev, couponCodes: result.codes }))
    persist(result.codes)
    keepInWallet(result.code)
    return { ok: true }
  }

  function pick(code: string) {
    setState((prev) => ({ ...prev, couponCodes: [code] }))
    persist([code])
  }

  return { coupons, focus: bagCouponFocus(coupons), remove, submitCode, pick }
}
